import { useMemo, useEffect } from 'react';
import { throttle } from 'lodash';
import { useWorkflowStore } from '@/state/workflow.state';
import { useFlowView } from '../FlowContext';
import type { Node } from '@/types/node';

const THROTTLE_TIME_MS = 16;

/**
 * Shared hook for highlighting potential attachements to groups during drag and resize operations.
 * Provides throttled highlighting (60fps) and cleanup functionality. Throttling is used because filtering through all nodes is expensive
 * and because it doesn't affect UX.
 */
export const useGroupAttachementsHighlighting = () => {
  const flowView = useFlowView();
  const setHighlightedNodeIds = useWorkflowStore((state) => state.setHighlightedNodeIds);
  const clearHighlightedNodeIds = useWorkflowStore((state) => state.clearHighlightedNodeIds);

  const highlightPotentialGroupAttachements = useMemo(
    () =>
      throttle(
        (groupNodes: Node[], ungroupedNodes: Node[] = []) => {
          const groupAttachments = flowView.findUngroupedNodesIntersectingWithGroups(groupNodes);
          const nodeGroupId = flowView.findGroupIntersectingWithNodes(ungroupedNodes);

          const allNodesToHighlight = [
            ...groupAttachments.flatMap((a) => a.nodes.map((n) => n.id)),
            ...(nodeGroupId ? [nodeGroupId] : []),
          ];

          setHighlightedNodeIds(allNodesToHighlight);
        },
        THROTTLE_TIME_MS,
        { leading: true, trailing: true },
      ),
    [flowView, setHighlightedNodeIds],
  );

  useEffect(() => {
    return () => {
      clearHighlightedNodeIds();
      highlightPotentialGroupAttachements.cancel();
    };
  }, [highlightPotentialGroupAttachements, clearHighlightedNodeIds]);

  return {
    highlightPotentialGroupAttachements,
    clearAttachementHighlights: clearHighlightedNodeIds,
  };
};
